import { CommonModule } from "@angular/common";

import { Component, OnDestroy, effect, inject, signal } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { passwErrors } from "@interfaces/interfaces";
import { IonicModule } from "@ionic/angular";
import { DataByTokenAndLoginName } from "@models/I_DecouplingTimeRecoverPassword";
import { GlobalVarsModel } from "@models/ModelGlobalsVar";
import { RegistroValidatorsService } from "@mso-services/registro/registroValidators.service";
import { UtilsMsoService } from "@mso-services/utilsMso.service";
import { TranslateModule } from "@ngx-translate/core";
import { DecouplingUserServiceMSO } from "@providers/DecouplingUserServiceMSO.service";
import { NewBaseService } from "@providers/newBase.service";
import { CodereFooterComponent } from "@shared-mso-components/footer/codere-footer/codere-footer";
import { UserSectionTitle } from "@shared-mso-components/user-section-title/user-section-title";
import { Subject, map, takeUntil } from "rxjs";

interface UserForm {
  password: FormControl<string>;
  passwordConfirm: FormControl<string>;
}

interface UrlDataParams {
  token: string;
  username: string;
}

enum DefaultUserErrors {
  REQUIRED = "required",
  MIN = "minlength",
  MAX = "maxlength",
  INVALID = "invalidPassword",
  DIFFERENT = "fieldsNotMatch",
}

enum UrlParams {
  TOKEN = "token",
  USER_NAME = "username",
}

const MAX_LENGTH = 20;
const MIN_LENGTH = 8;

@Component({
  standalone: true,
  templateUrl: "./reset-password-page.html",
  selector: "reset-password-page",
  styleUrls: ["./reset-password-page.scss"],
  imports: [
    IonicModule,
    CommonModule,
    TranslateModule,
    CodereFooterComponent,
    UserSectionTitle,
    ReactiveFormsModule,
  ],
})
export class ResetPasswordPage implements OnDestroy {
  userForm: FormGroup<UserForm>;

  showPassword: { [key: string]: boolean } = {
    password: false,
    confirmPassword: false,
  };

  globalVars!: GlobalVarsModel;
  formBuilder = inject(FormBuilder);
  newBaseService = inject(NewBaseService);
  router = inject(Router);
  activatedRouter = inject(ActivatedRoute);
  decouplingUserServiceMso = inject(DecouplingUserServiceMSO);
  utilsMsoService = inject(UtilsMsoService);
  registroValidatorsService = inject(RegistroValidatorsService);
  invalidUserToken = signal(false);
  urlParams = toSignal<UrlDataParams | undefined>(
    this.activatedRouter.queryParamMap.pipe(
      map((params) => ({
        token: params.get(UrlParams.TOKEN) ?? "",
        username: params.get(UrlParams.USER_NAME) ?? "",
      })),
    ),
  );

  paramsEffect = effect(
    () => {
      const params = this.urlParams();
      if (params) {
        if (!params.token || !params.username) {
          this.router.navigate(["/"]);
        } else {
          this.getUserByToken(params);
        }
      }
    },
    { allowSignalWrites: true },
  );

  private readonly unsubscribe$ = new Subject();

  constructor() {
    this.newBaseService.getVars.pipe(takeUntil(this.unsubscribe$)).subscribe((data: GlobalVarsModel) => {
      this.globalVars = data;
    });
    this.initialiceForm();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next(null);
    this.unsubscribe$.complete();
  }

  initialiceForm() {
    this.userForm = this.formBuilder.group({
      password: new FormControl("", {
        updateOn: "blur",
      }),
      passwordConfirm: new FormControl("", {
        updateOn: "blur",
      }),
    });

    this.setFormEvents();
  }

  getUserByToken({ token, username }: UrlDataParams) {
    this.invalidUserToken.set(false);
    this.decouplingUserServiceMso
      .getUserDataByTokenAndLoginName({ token, loginUserName: username })
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (response) => {
          this.setFormValidators(response);
        },
        error: () => {
          this.invalidUserToken.set(true);
        },
      });
  }

  verificarCoincidencia(field: string): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control || !this.userForm || !this.userForm.controls[field]) {
        return null;
      }
      const fristValue = control.value;
      const confirmPassword = this.userForm.controls[field].value;
      return fristValue === confirmPassword ? null : { fieldsNotMatch: true };
    };
  }

  setAllControllsTouchedDirty(formGroup: FormGroup) {
    Object.keys(formGroup.controls).forEach((field) => {
      const value = formGroup.get(field);
      value.markAsTouched();
      value.markAsDirty();
    });
  }

  toggleTypeInput(field: string) {
    this.showPassword[field] = !this.showPassword[field];
  }

  setFormEvents() {
    this.userForm.controls.passwordConfirm.valueChanges.pipe(takeUntil(this.unsubscribe$)).subscribe((_) => {
      this.userForm.controls.password.updateValueAndValidity({
        emitEvent: false,
      });
    });
    this.userForm.controls.password.valueChanges.pipe(takeUntil(this.unsubscribe$)).subscribe((_) => {
      this.userForm.controls.passwordConfirm.updateValueAndValidity({
        emitEvent: false,
      });
    });
  }

  setFormValidators(dataUser: DataByTokenAndLoginName) {
    const { firstName, firstSurName, playerUserName, secondSurName, birthDate } = dataUser;
    const birthDateFormat = new Date(birthDate);
    this.userForm.reset();
    const commonValidators = [
      Validators.required,
      Validators.minLength(MIN_LENGTH),
      Validators.maxLength(MAX_LENGTH),
      this.registroValidatorsService.validationPasswordFromUserData({
        name: firstName,
        loginUserName: playerUserName,
        surname: firstSurName,
        surname2: secondSurName,
        birthDate: {
          day: birthDateFormat.getDate(),
          moth: birthDateFormat.getMonth() + 1,
          year: birthDateFormat.getFullYear(),
        },
      }),
    ];
    this.userForm.controls.password.setValidators(commonValidators);
    this.userForm.controls.passwordConfirm.setValidators([
      ...commonValidators,
      this.verificarCoincidencia("password"),
    ]);
  }

  openLogin(gotoRecovery = false) {
    this.globalVars.gotorecovery = gotoRecovery;
    this.router.navigate(["/"]);
    this.globalVars.rootScope.openLogin();
  }

  ionViewDidLeave() {
    this.userForm?.reset();
  }

  resetPassword() {
    this.setAllControllsTouchedDirty(this.userForm);

    if (this.userForm.valid) {
      const { token, username: playerUserName } = this.urlParams();
      this.decouplingUserServiceMso
        .resetPassword({
          token,
          newPassword: this.utilsMsoService.encryptWithPublicKey(
            this.userForm.controls.password.value,
            "MSO_rsa_publickey",
          ),
          playerUserName,
        })
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe({
          next: () => {
            this.openLogin();
          },
          error: () => {
            this.invalidUserToken.set(true);
          },
        });
    }
  }

  getErrors(control: FormControl) {
    if (!control) {
      return "";
    }
    const error = control.errors;
    if (!error) {
      return "";
    }
    let errorCode: passwErrors;

    switch (true) {
      case error[DefaultUserErrors.REQUIRED] !== undefined:
        errorCode = passwErrors.requiered;
        break;
      case error[DefaultUserErrors.MAX] !== undefined:
        errorCode = passwErrors.tooLong;
        break;
      case error[DefaultUserErrors.MIN] !== undefined:
        errorCode = passwErrors.tooShort;
        break;
      case error[DefaultUserErrors.DIFFERENT] !== undefined:
        errorCode = passwErrors.differentPass;
        break;
      case error[DefaultUserErrors.INVALID] !== undefined:
        errorCode = error[DefaultUserErrors.INVALID];
        break;
      default:
        return "";
    }

    return this.registroValidatorsService.getErrorLiteral(errorCode);
  }
}
